import { Pog } from "@CommonTypes/merchflow/pog/pog";
import { useQuery } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useApi } from "src/api";
import { isCancel } from "axios";
import { useSearchParams } from "src/utils/useSearchParams";

import { RouteGetMerchflowBasePog_basePogId_ } from "@CommonApi/merchflow/base-pog/_basePogId_";
import { RouteGetMerchflowStorePog_storePogId_ } from "@CommonApi/merchflow/store/pog/_storePogId_";
import { RouteGetMerchflowStorePog_storePogId_previousAssortment } from "@CommonApi/merchflow/store/pog/_storePogId_/previous-assortment";
import { RouteCategories_categoryId_PdfSummaryExclusions } from "@CommonApi/categories/_categoryId_/pdf-summary-exclusions";
import { FlowReview } from "@CommonTypes/backend/FlowReview";
import { Page, PageStatus } from "src/components";
import { useLoaders } from "src/components/InlineLoader";
import { useTranslation } from "src/i18n";
import { omniAtom } from "src/utils/atoms";
import { PdfPlanogramPage } from "./PdfPlanogramPage";
import { sanitizeStoreTitle } from "./singleBayUtils";

const pogAtom = omniAtom<Pog | null>({
  key: "pages/pogExportPdf/pog",
  value: null,
});

export type StoreDetail = Pick<
  FlowReview,
  "category_code" | "retailer_category_code" | "category_id"
> & {
  store: string;
  name: string;
  go_live_date: string | null;
  template_id?: string | null;
};

export type StoreProductsMap = Record<string, string | { name: string; is_oos?: boolean }>;

export const PageMerchflows_ExportPdf_StorePogId = () => {
  const { storePogId: _storePogId, basePogId: _basePogId } = useParams<{
    storePogId?: string;
    basePogId?: string;
  }>();
  const storePogId = Number(_storePogId);
  const basePogId = Number(_basePogId);
  const { getSearchParam } = useSearchParams();
  const compareWithParam = getSearchParam("compareWith");
  const compareWith = compareWithParam ? Number(compareWithParam) : undefined;

  const {
    getMerchflowStorePogApi,
    getMerchflowBasePogApi,
    getMerchflowByFilterConfigIdApi,
    getMerchflowDetailsApi,
    getPreviousAssortmentApi,
    getPdfSummaryProductExclusionsApi,
  } = useApi();
  const { hasLoader } = useLoaders();
  const { t } = useTranslation("planogramDelivery");

  const [storeDetail, setStoreDetail] = useState<StoreDetail | null>(null);
  const [pog, setPog] = useAtom(pogAtom);
  const [storeProducts, setStoreProducts] = useState<StoreProductsMap | undefined>(undefined);
  const [hasNoDeliveryHistory, setHasNoDeliveryHistory] = useState(false);
  const [hasComparisonError, setHasComparisonError] = useState(false);
  const [loadedRouteKey, setLoadedRouteKey] = useState<string | null>(null);
  const [readyLayoutInput, setReadyLayoutInput] = useState<object | null>(null);
  const [failedLayoutInput, setFailedLayoutInput] = useState<object | null>(null);
  const [storeDetailState, setStoreDetailState] = useState<"loading" | "ready" | "error">(
    "loading",
  );
  const routeKey = `${storePogId}:${basePogId}`;

  const categoryId = storeDetail?.category_id;
  const hasValidCategoryId = typeof categoryId === "number" && categoryId > 0;
  const exclusionsQuery = useQuery({
    queryKey: ["pdf-summary-product-exclusions", categoryId],
    queryFn: async () => {
      if (!hasValidCategoryId) throw new Error("Missing PDF summary exclusions category");

      const response = await getPdfSummaryProductExclusionsApi(categoryId);
      if (!response) throw new Error("Failed to load PDF summary exclusions");

      return response;
    },
    enabled: hasValidCategoryId,
    retry: 1,
    retryDelay: 300,
  });

  const isLoading =
    hasLoader(RouteGetMerchflowStorePog_storePogId_) ||
    hasLoader(RouteGetMerchflowBasePog_basePogId_) ||
    hasLoader(RouteGetMerchflowStorePog_storePogId_previousAssortment) ||
    hasLoader(RouteCategories_categoryId_PdfSummaryExclusions) ||
    exclusionsQuery.isPending;
  const isCurrentRouteLoaded = loadedRouteKey === routeKey;
  const hasExportError =
    (isCurrentRouteLoaded && storeDetailState === "error") ||
    (isCurrentRouteLoaded && storeDetailState === "ready" && !hasValidCategoryId) ||
    (isCurrentRouteLoaded && exclusionsQuery.isError);
  const isReady =
    isCurrentRouteLoaded &&
    !hasExportError &&
    !isLoading &&
    storeDetailState === "ready" &&
    hasValidCategoryId &&
    Boolean(pog) &&
    exclusionsQuery.isSuccess &&
    Boolean(exclusionsQuery.data);
  const layoutInput = useMemo(
    () => ({
      pog,
      storeDetail,
      storeProducts,
      hasNoDeliveryHistory,
      hasComparisonError,
      excludedProductCodes: exclusionsQuery.data?.product_codes,
    }),
    [
      pog,
      storeDetail,
      storeProducts,
      hasNoDeliveryHistory,
      hasComparisonError,
      exclusionsQuery.data?.product_codes,
    ],
  );
  const onLayoutReady = useCallback(() => setReadyLayoutInput(layoutInput), [layoutInput]);
  const onLayoutError = useCallback(
    (error: Error) => {
      console.error("Failed to paginate PDF tables:", error);
      setFailedLayoutInput(layoutInput);
    },
    [layoutInput],
  );
  const hasLayoutError = failedLayoutInput === layoutInput;
  const renderState =
    hasExportError || hasLayoutError
      ? "error"
      : isReady && readyLayoutInput === layoutInput
        ? "ready"
        : "loading";
  const errorLabel =
    hasLayoutError || storeDetailState === "error"
      ? t("pdfExport.loadError")
      : !hasValidCategoryId
        ? t("pdfExport.missingCategory")
        : t("pdfExport.exclusionsError");

  useEffect(() => {
    let active = true;

    setStoreDetailState("loading");
    setLoadedRouteKey(null);
    setStoreDetail(null);
    setStoreProducts(undefined);
    setHasNoDeliveryHistory(false);
    setHasComparisonError(false);
    setPog(null);

    const loadPog = async () => {
      try {
        let response;
        let nextStoreDetail: StoreDetail | null = null;
        if (storePogId) {
          response = await getMerchflowStorePogApi(storePogId, false);
          const merchflowDetail = await getMerchflowByFilterConfigIdApi(
            response?.filter_config_id || 0,
          );
          nextStoreDetail = {
            store: response?.store || "",
            category_code: merchflowDetail?.category_code || "",
            retailer_category_code: merchflowDetail?.retailer_category_code || "",
            category_id: merchflowDetail?.category_id ?? null,
            name: merchflowDetail?.name || "",
            go_live_date: merchflowDetail?.go_live_date || null,
            template_id: response?.store_template_id || response?.template_id || null,
          };
        } else if (basePogId) {
          response = await getMerchflowBasePogApi(basePogId);
          const merchflowDetail = await getMerchflowDetailsApi(response?.merchflow_id || 0);
          nextStoreDetail = {
            store: response?.store_code || "",
            category_code: merchflowDetail?.category_code || "",
            retailer_category_code: merchflowDetail?.retailer_category_code || "",
            category_id: merchflowDetail?.category_id ?? null,
            name: merchflowDetail?.name || "",
            go_live_date: merchflowDetail?.go_live_date || null,
            template_id: null,
          };
        }

        if (!active) return;
        if (!response || !response.pog || !nextStoreDetail) {
          setLoadedRouteKey(routeKey);
          setStoreDetailState("error");
          return;
        }

        setPog(new Pog(response.pog));
        setStoreDetail(nextStoreDetail);
        setLoadedRouteKey(routeKey);
        setStoreDetailState("ready");
      } catch (error) {
        if (!active) return;
        console.error("Failed to load PDF export data:", error);
        setLoadedRouteKey(routeKey);
        setStoreDetailState("error");
      }
    };

    void loadPog();

    return () => {
      active = false;
      setPog(null);
    };
  }, [basePogId, routeKey, storePogId]);

  useEffect(() => {
    if (!storeDetail) return;

    const previousTitle = document.title;
    const sanitizedTitle = sanitizeStoreTitle(
      storeDetail.category_code,
      storeDetail.store,
      storeDetail.template_id,
    );
    document.title = sanitizedTitle;

    return () => {
      document.title = previousTitle;
    };
  }, [storeDetail]);

  useEffect(() => {
    if (!storePogId) return;

    let active = true;
    const controller = new AbortController();

    // This page also renders headless, unattended, for the batch PDF export — there's no one
    // to notice a one-off network blip and retry manually, and the alternative (silently
    // treating any failure as "everything is new") would produce a misleading document for
    // what's often just a transient error. One retry before giving up.
    const fetchPreviousAssortment = async () => {
      try {
        return await getPreviousAssortmentApi({
          storePogId,
          compareWith,
          signal: controller.signal,
        });
      } catch (err) {
        if (isCancel(err)) throw err;
        return await getPreviousAssortmentApi({
          storePogId,
          compareWith,
          signal: controller.signal,
        });
      }
    };

    fetchPreviousAssortment()
      .then((data) => {
        if (!active || !data) return;

        setHasComparisonError(false);

        // No delivery on record at all is treated the same as a real previous delivery that
        // happened to have zero products — every current item shows as newly added either way
        // — but we still flag it separately so the PDF can say so explicitly, rather than
        // looking identical to "compared against an empty delivery."
        setHasNoDeliveryHistory(data.source === "NONE");

        const productsMap: StoreProductsMap = {};
        for (const product of data.products) {
          productsMap[product.product_code] = { name: product.name, is_oos: false };
        }
        setStoreProducts(productsMap);
      })
      .catch((err) => {
        if (isCancel(err)) return;
        console.error("Failed to fetch previous assortment:", err);

        // getPreviousAssortmentApi already toasts, but that's transient - if this PDF gets
        // saved/printed there'd be no lasting record that the comparison failed. Treat it like
        // an empty delivery (every item shows as new) rather than leaving storeProducts
        // undefined, which would silently fall back to an unrelated legacy new/deleted-flag
        // mechanism instead of clearly explaining why nothing was compared.
        if (!active) return;
        setHasComparisonError(true);
        setStoreProducts({});
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [storePogId, compareWith]);

  return (
    <Page hasSidebar={false}>
      <div data-pdf-render-state={renderState}>
        {renderState === "loading" && <PageStatus label={t("pdfExport.loading")} icon="spinner" />}
        {renderState === "error" && <PageStatus icon="alert" label={errorLabel} />}
        {isReady && !hasLayoutError && exclusionsQuery.data && storeDetail && (
          <PdfPlanogramPage
            pogAtom={pogAtom}
            storeDetail={storeDetail}
            storeProducts={storeProducts}
            hasNoDeliveryHistory={hasNoDeliveryHistory}
            hasComparisonError={hasComparisonError}
            excludedProductCodes={exclusionsQuery.data.product_codes}
            onLayoutReady={onLayoutReady}
            onLayoutError={onLayoutError}
          />
        )}
      </div>
    </Page>
  );
};
